import { useMemo, useState } from 'react'

import { useTranslations } from 'next-intl'

import classnames from 'classnames'
import classNames from 'classnames'

import type { ArticlePreviewCardProps } from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import ArticlePreviewCard from '~/components/atoms/ArticlePreviewCard/ArticlePreviewCard'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import DynamicSlider from '~/components/atoms/DynamicSlider'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Link from '~/components/atoms/Link/Link'
import Lozenge, { LozengeVariant } from '~/components/atoms/Lozenge/Lozenge'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'
import type { TSearchAnalystEventProps } from '~/services/elastic/analyst'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'
import articleParentTitleLink from '~/utils/articleParentTitleLink'

import LozengeList from '../LozengeList/LozengeList'

import styles from './RelatedArticles.module.css'

export interface RelatedArticlesProps {
  articles: ArticlePreviewCardProps[]
  theme?: ComponentTheme
  baseTheme?: ComponentTheme
  testID?: string
  title?: string
  sectionId?: string
  noPaddingBottom?: boolean
  noPaddingTop?: boolean
  searchInsightsEvent?: TSearchAnalystEventProps
  hideTitle?: boolean
  hideArrows?: boolean
  hideViewAll?: boolean
  disableFilters?: boolean
  hideBrowse?: boolean
  borderTitle?: boolean
  hideAuthors?: boolean
  browseButtonFilter?: string
  testAttr?: string
  moduleId?: string
}

export default function RelatedArticles({
  articles,
  theme = ComponentTheme.primary,
  baseTheme,
  testID,
  sectionId,
  noPaddingBottom,
  noPaddingTop,
  hideArrows = false,
  searchInsightsEvent,
  hideViewAll = false,
  disableFilters = false,
  hideBrowse = false,
  title,
  borderTitle = true,
  hideAuthors = false,
  browseButtonFilter,
  testAttr = 'related-articles',
  moduleId,
}: RelatedArticlesProps) {
  const t = useTranslations('common')
  const isMobile = useMediaQuery(`(max-width: ${bp.bpLarge})`)
  const [currentFilter, setCurrentFilter] = useState<{
    name: string
    link: string
  } | null>(null)
  const [filterChanged, setFilterChanged] = useState(0)
  let baseThemeClass = baseTheme
  if (theme === ComponentTheme.magazine && !baseTheme)
    baseThemeClass = ComponentTheme.magazine

  const categoryList = useMemo(() => {
    const categories: {
      name: string
      link: string
      order: number | null
    }[] = []

    articles?.forEach((item: any) => {
      // Add secondary categories (if they exist)
      if (item.secondaryCategories) {
        item.secondaryCategories.forEach((sItem: any) => {
          categories.push({
            name: sItem.name,
            order: sItem.categoryOrder,
            link: `/articles/${sItem.slug}`,
          })
        })
      }

      // Always add the main parent category (not only as an "else" case)
      const articleParentTitle = articleParentTitleLink(
        item.articleType || '',
        item.articleTypeSlug || '',
        item.articleTaxonomy,
        item.articleTypeTitle
      )

      if (
        item.articleType !== 'artist' &&
        articleParentTitle.title &&
        articleParentTitle.link
      ) {
        categories.push({
          name: articleParentTitle.title,
          link: articleParentTitle.link,
          order: item.categoryOrder,
        })
      }
    })

    const uniqueCategories = categories.filter(
      (item, index, self) =>
        self.findIndex(obj => obj.name === item.name) === index
    )

    // sort categories by their category order property
    uniqueCategories.sort((a, b) => {
      if (a.order === null) return 1
      if (b.order === null) return -1
      return a.order - b.order
    })

    return uniqueCategories
  }, [articles])

  const articleList: ArticlePreviewCardProps[] = useMemo(() => {
    if (currentFilter !== null) {
      return articles.filter((article: any) => {
        // Check if article matches the filter in secondary categories
        const matchesSecondaryCategories =
          article.secondaryCategories &&
          article.secondaryCategories.some(
            (category: { name: string }) => category.name === currentFilter.name
          )

        // Check if article matches the filter in main parent category
        const articleParentTitle = articleParentTitleLink(
          article.articleType || '',
          article.articleTypeSlug || '',
          article.articleTaxonomy,
          article.articleTypeTitle
        )

        const matchesMainCategory =
          articleParentTitle.title === currentFilter.name

        return matchesSecondaryCategories || matchesMainCategory
      })
    } else {
      return articles
    }
  }, [articles, currentFilter])

  const renderFilterViewAll = () => {
    return (
      currentFilter !== null && (
        <Link
          href={currentFilter.link}
          className={styles.viewAllButtonContainer}
          onClick={async () => {
            await multiPostHogEventPusher([
              {
                eventType: `View all ${currentFilter?.name} clicked`,
              },
            ])
          }}
        >
          <Button
            variant={ButtonVariants.secondaryDark}
            button={{
              type: ButtonTypes.button,
            }}
            className={styles.viewAllButton}
          >
            {t('viewAllCategory', { category: currentFilter?.name })}
          </Button>
        </Link>
      )
    )
  }

  return (
    <div
      className={classnames(
        styles.base,
        baseThemeClass && styles[baseThemeClass]
      )}
      data-testid={testID}
      data-test={testAttr}
      id={moduleId}
    >
      <Fixture
        theme={theme}
        verticalPadding={theme !== ComponentTheme.magazine}
        noPaddingBottom={noPaddingBottom}
        noPaddingTop={noPaddingTop}
      >
        <div style={{ overflow: 'hidden' }}>
          <div className={styles.wrap}>
            <Container
              cssClass={classnames(styles.sliderContainer, styles[theme])}
            >
              <div
                className={classNames(
                  styles.slideHead,
                  !borderTitle && styles.slideHeadNoBorder
                )}
              >
                {title ? (
                  <Text
                    size={TextSizes.primaryTitleLrg}
                    className={styles.slideHeadTitle}
                  >
                    {title}
                  </Text>
                ) : (
                  <Text
                    size={TextSizes.magSecondaryBodyLrg}
                    className={classNames(
                      styles.slideHeadTitle,
                      styles.slideHeadEditionTitle
                    )}
                  >
                    {t('editionMagazine')}
                  </Text>
                )}

                {!hideBrowse && (
                  <Link
                    href={`/articles${
                      browseButtonFilter ? `?${browseButtonFilter}` : ''
                    }`}
                    prefetch={false}
                    onClick={async () => {
                      await multiPostHogEventPusher([
                        {
                          eventType: `Edition Magazine Browse clicked`,
                        },
                      ])
                    }}
                  >
                    <Text
                      size={TextSizes.primaryTitleLrg}
                      className={styles.slideHeadBrowse}
                    >
                      {t('browse')}
                    </Text>
                  </Link>
                )}
              </div>
              {!disableFilters && (
                <div className={styles.filterContainer}>
                  <LozengeList
                    className={classNames(styles.filtersLozengeList)}
                    // stack={false}
                  >
                    {categoryList.map(
                      (category: { name: string; link: string }, i: number) => (
                        <Lozenge
                          title={category.name}
                          key={`category-lozenge-${i}`}
                          variant={LozengeVariant.gray}
                          onClick={async e => {
                            e.preventDefault()
                            setFilterChanged(filterChanged + 1)
                            setCurrentFilter(
                              currentFilter !== null &&
                                currentFilter?.name === category.name
                                ? null
                                : category
                            )
                            await multiPostHogEventPusher([
                              {
                                eventType: `${category.name} Articles filter clicked`,
                              },
                            ])
                          }}
                          isActive={currentFilter?.name === category.name}
                          count={
                            currentFilter?.name === category.name ? 'x' : ''
                          }
                        />
                      )
                    )}
                  </LozengeList>
                  {currentFilter && !isMobile && renderFilterViewAll()}
                </div>
              )}

              <DynamicSlider
                slidesPerView={1.4}
                slidesPerViewMed={2.4}
                slidesPerViewLrg={3.4}
                overflowVisible={true}
                sectionId={sectionId}
                hideTitle={true}
                hideArrows={hideArrows}
                hideArrowsMobile={true}
                useScrollbar
                spacer={false}
                wrapperClass={styles.sliderWrapperClass}
                initiateInitialSlide={filterChanged}
              >
                {articleList?.map(
                  (item: ArticlePreviewCardProps, i: number) => {
                    return (
                      <ArticlePreviewCard
                        {...item}
                        title={`${item.title}`}
                        category={t('guide')}
                        modules={item.modules}
                        imageType="landscape"
                        summaryLimit={200}
                        showReadMore={theme === ComponentTheme.magazine}
                        hideSummary={theme === ComponentTheme.magazine}
                        variant={
                          theme === ComponentTheme.magazine
                            ? 'magazine'
                            : 'medium'
                        }
                        theme={theme}
                        leadImage={item.leadImage}
                        listPreviewImage={item?.listPreviewImage}
                        hideAuthor={hideAuthors}
                        key={`related-articles-${i}`}
                        titleTag="h3"
                        sizes="(max-width: 559px) 100vw, (max-width: 1079px) 33vw, 25vw"
                        searchInsightsEvent={searchInsightsEvent}
                        testAttr={`${testAttr}-item`}
                        noPrefetch={true}
                        landscapeThumbnails={true}
                      />
                    )
                  }
                )}
              </DynamicSlider>
              {currentFilter && isMobile && (
                <div className={styles.mobileFilterViewAll}>
                  {renderFilterViewAll()}
                </div>
              )}
            </Container>
          </div>
        </div>
      </Fixture>
    </div>
  )
}
